/**
 * Rate utility functions
 */

// Map field names to their isBlackout keys
const blackoutMap = {
  // Daily base
  dailybaseRate: 'dailybaseIsBlackout',
  // Weekend base
  weekendbaseRate: 'weekendbaseIsBlackout',
  // Weekly base
  weeklybaseRate: 'weeklybaseIsBlackout',
  // Monthly base
  monthlybaseRate: 'monthlybaseIsBlackout',
  // Daily extra
  dailyextraRate: 'dailyextraIsBlackout',
  // Weekend extra
  weekendextraRate: 'weekendextraIsBlackout',
  // Weekly extra
  weeklyextraRate: 'weeklyextraIsBlackout',
  // Monthly extra
  monthlyextraRate: 'monthlyextraIsBlackout',
  // Daily hours
  dailyhoursRate: 'dailyhoursIsBlackout',
  // Weekend hours
  weekendhoursRate: 'weekendhoursIsBlackout',
  // Weekly hours
  weeklyhoursRate: 'weeklyhoursIsBlackout',
  // Monthly hours
  monthlyhoursRate: 'monthlyhoursIsBlackout',
  // Daily LOR
  dailylor1st: 'dailylor1stIsBlackout',
  dailylor2nd: 'dailylor2ndIsBlackout',
  dailylor3rd: 'dailylor3rdIsBlackout',
  dailylor4th: 'dailylor4thIsBlackout',
  dailylor5th: 'dailylor5thIsBlackout',
  dailylor6th: 'dailylor6thIsBlackout',
  // Weekend LOR
  weekendlor1st: 'weekendlor1stIsBlackout',
  weekendlor2nd: 'weekendlor2ndIsBlackout',
  weekendlor3rd: 'weekendlor3rdIsBlackout',
  weekendlor4th: 'weekendlor4thIsBlackout',
  weekendlor5th: 'weekendlor5thIsBlackout',
  weekendlor6th: 'weekendlor6thIsBlackout',
  // Weekly LOR
  weeklylor1st: 'weeklylor1stIsBlackout',
  weeklylor2nd: 'weeklylor2ndIsBlackout',
  weeklylor3rd: 'weeklylor3rdIsBlackout',
  weeklylor4th: 'weeklylor4thIsBlackout',
};

// Map field names to their ID keys (for checking recently changed fields)
const fieldIdMap = {
  // Daily base
  dailybaseRate: 'dailybaseId',
  // Weekend base
  weekendbaseRate: 'weekendbaseId',
  // Weekly base
  weeklybaseRate: 'weeklybaseId',
  // Monthly base
  monthlybaseRate: 'monthlybaseId',
  // Daily extra
  dailyextraRate: 'dailyextraId',
  // Weekend extra
  weekendextraRate: 'weekendextraId',
  // Weekly extra
  weeklyextraRate: 'weeklyextraId',
  // Monthly extra
  monthlyextraRate: 'monthlyextraId',
  // Daily hours
  dailyhoursRate: 'dailyhoursId',
  // Weekend hours
  weekendhoursRate: 'weekendhoursId',
  // Weekly hours
  weeklyhoursRate: 'weeklyhoursId',
  // Monthly hours
  monthlyhoursRate: 'monthlyhoursId',
  // Daily LOR
  dailylor1st: 'dailylor1stId',
  dailylor2nd: 'dailylor2ndId',
  dailylor3rd: 'dailylor3rdId',
  dailylor4th: 'dailylor4thId',
  dailylor5th: 'dailylor5thId',
  dailylor6th: 'dailylor6thId',
  // Weekend LOR
  weekendlor1st: 'weekendlor1stId',
  weekendlor2nd: 'weekendlor2ndId',
  weekendlor3rd: 'weekendlor3rdId',
  weekendlor4th: 'weekendlor4thId',
  weekendlor5th: 'weekendlor5thId',
  weekendlor6th: 'weekendlor6thId',
  // Weekly LOR
  weeklylor1st: 'weeklylor1stId',
  weeklylor2nd: 'weeklylor2ndId',
  weeklylor3rd: 'weeklylor3rdId',
  weeklylor4th: 'weeklylor4thId',
};

export const getFieldIdKey = (fieldName) => {
  return fieldIdMap[fieldName];
};

export const isFieldRecentlyChanged = (fieldName, formFieldValueMap) => {
  const fieldIdKey = getFieldIdKey(fieldName);
  if (!fieldIdKey) return false;
  
  const fieldId = formFieldValueMap?.[fieldIdKey];
  const changedFieldIds = formFieldValueMap?.changedFieldIds || [];
  
  return fieldId && changedFieldIds.includes(fieldId);
};

export const parseChangedFieldIds = (lastChangeLog) => {
  if (!lastChangeLog || !lastChangeLog.changedFields) {
    return [];
  }
  
  try {
    const changedFields = typeof lastChangeLog.changedFields === 'string' 
      ? JSON.parse(lastChangeLog.changedFields) 
      : lastChangeLog.changedFields;
    
    const changedIds = [];
    
    // Extract IDs from rateBaseDTO
    if (changedFields.rateBaseDTO) {
      changedFields.rateBaseDTO.forEach(item => {
        if (item.id) changedIds.push(item.id);
      });
    }
    
    // Extract IDs from rateExtraDayDTOS
    if (changedFields.rateExtraDayDTOS) {
      changedFields.rateExtraDayDTOS.forEach(item => {
        if (item.id) changedIds.push(item.id);
      });
    }
    
    // Extract IDs from rateExtraHourDTOS
    if (changedFields.rateExtraHourDTOS) {
      changedFields.rateExtraHourDTOS.forEach(item => {
        if (item.id) changedIds.push(item.id);
      });
    }
    
    // Extract IDs from rateLorAdjustmentDTOS
    if (changedFields.rateLorAdjustmentDTOS) {
      changedFields.rateLorAdjustmentDTOS.forEach(item => {
        if (item.id) changedIds.push(item.id);
      });
    }
    
    return changedIds;
  } catch (e) {
    console.error('Error parsing changedFields:', e);
    return [];
  }
};

export const getBlackoutKey = (fieldName) => {
  return blackoutMap[fieldName];
};

export const isFieldBlackout = (fieldName, formFieldValueMap) => {
  const blackoutKey = getBlackoutKey(fieldName);
  return blackoutKey ? !!formFieldValueMap?.[blackoutKey] : false;
};


export const formatBlackoutContent = (blackoutDates) => {
  if (!blackoutDates || blackoutDates.length === 0) {
    return '';
  }
  
  const items = blackoutDates.map((blackout) => (
    `<div class="blackout-tooltip-item"><div><strong>Blackout Period:</strong></div><div>StartDate: ${blackout.startDate} ${blackout.startTime}</div><div>EndDate: ${blackout.endDate} ${blackout.endTime}</div>${blackout.blackoutMessage ? `<div><strong>Message:</strong> ${blackout.blackoutMessage}</div>` : ''}<div><span class="${blackout.pickupAllowed ? 'allowed' : 'not-allowed'}">Pickup: ${blackout.pickupAllowed ? 'Yes' : 'No'}</span> <span class="${blackout.dropoffAllowed ? 'allowed' : 'not-allowed'}">Dropoff: ${blackout.dropoffAllowed ? 'Yes' : 'No'}</span> <span class="${blackout.spanoverAllowed ? 'allowed' : 'not-allowed'}">Spanover: ${blackout.spanoverAllowed ? 'Yes' : 'No'}</span></div></div>`
  )).join('');
  
  // Wrap in flex container if multiple blackouts - stack vertically
  if (blackoutDates.length > 1) {
    return `<div class="blackout-tooltip-flex">${items}</div>`;
  }
  
  return items;
};
